// Tiled looping video layer — the animated counterpart of the CSS background.
//
// CSS can tile an image but not a <video>, so one offscreen video element is
// decoded once and painted as a grid onto a fixed canvas sitting directly
// above the background layers (and below every scrim and panel, which keep
// their z-index). The grid reproduces the background's own geometry —
// `background-size: var(--tile-size)` at `calc(50% + var(--tile-x/y))` — by
// reading the same tile size and pan variables each frame, so the animated
// wall lands exactly over the still tiling it replaces and the zoom slider
// keeps working while the pattern moves.

import { getTileSize } from './tilesize.js';

const canvas = document.getElementById('videowall');
const ctx = canvas.getContext('2d');

const video = document.createElement('video');
video.loop = true;
video.playsInline = true;
video.preload = 'auto';

let raf = 0;
let active = false;

function paint() {
  raf = requestAnimationFrame(paint);
  if (video.readyState < 2) return; // no decoded frame yet

  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(window.innerWidth * dpr);
  const h = Math.round(window.innerHeight * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }

  // The background's exact geometry: with background-position 50%, a tile's
  // centre-point sits at the viewport centre plus the pan offset, so the grid
  // origin is that anchor minus half a tile, wrapped into view.
  const size = Math.max(1, getTileSize() * dpr);
  const tx = (parseFloat(document.body.style.getPropertyValue('--tile-x')) || 0) * dpr;
  const ty = (parseFloat(document.body.style.getPropertyValue('--tile-y')) || 0) * dpr;
  const wrap = (v) => ((v % size) + size) % size;
  const x0 = wrap(w / 2 + tx - size / 2) - size;
  const y0 = wrap(h / 2 + ty - size / 2) - size;

  for (let y = y0; y < h; y += size) {
    for (let x = x0; x < w; x += size) {
      // Overdraw by a hair to keep antialiased edges from showing the still
      // background through sub-pixel grid cracks.
      ctx.drawImage(video, x, y, size + 0.75, size + 0.75);
    }
  }
}

/**
 * Start playing `src` (an object URL or remote mp4) as the tiled wall.
 *
 * Resolves once playback has actually started. Unmuted autoplay is refused by
 * browsers outside a fresh user gesture — and a render finishes minutes after
 * the click that ordered it — so a refused unmuted start falls back to muted
 * playback rather than a frozen wall; the caller reads `isVideoWallMuted()`
 * afterwards to label its sound button truthfully.
 */
export async function showVideoWall(src, { muted = false } = {}) {
  if (video.src !== src) video.src = src;
  video.muted = muted;
  active = true;
  canvas.classList.add('show');
  if (!raf) raf = requestAnimationFrame(paint);
  try {
    await video.play();
  } catch {
    video.muted = true;
    await video.play().catch(() => {});
  }
}

/** Stop painting and release the decoder; the still background reappears. */
export function hideVideoWall() {
  active = false;
  canvas.classList.remove('show');
  if (raf) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  video.pause();
  video.removeAttribute('src');
  video.load();
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

export function isVideoWallActive() {
  return active;
}

export function isVideoWallMuted() {
  return video.muted;
}

/** Flip the soundtrack. Unmuting happens inside a real click, so the play()
 *  nudge (needed if the element sits paused after a muted-fallback) is
 *  gesture-blessed and will not be refused. */
export function setVideoWallMuted(muted) {
  video.muted = muted;
  if (active && video.paused) video.play().catch(() => {});
}
